import type { DemoAidaContent } from './demoComparisonTypes';

export const DEMO_AIDA_BY_DISEASE: Record<string, DemoAidaContent> = {
  'ehlers-danlos': {
    question: 'What genes are associated with Ehlers-Danlos syndrome?',
    title: 'Genes Associated with Ehlers-Danlos Syndrome',
    context:
      'Context: 123 known gene-disease associations for Ehlers-Danlos Syndrome (C0013720) reported in DISGENET.',
    sectionTitle: 'Key Genes and Their Relevance',
    entries: [
      {
        heading: 'COL5A1',
        lines: [
          'Score: 1.0',
          'Publications: 52',
          'Clinical Trials: None',
          'First Reported: 1995',
          'Expected Continuation: Until 2025',
          'This gene is crucial for collagen formation, impacting connective tissue integrity.',
        ],
      },
      {
        heading: 'TNXB',
        lines: [
          'Score: 1.0',
          'Publications: 51',
          'Clinical Trials: None',
          'First Reported: 1997',
          'Expected Continuation: Until 2025',
          "TNXB is involved in the structure of connective tissues, contributing to the disease's phenotypic manifestations.",
        ],
      },
      {
        heading: 'COL5A2',
        lines: [
          'Score: 1.0',
          'Publications: 20',
          'Clinical Trials: None',
          'First Reported: 1998',
          'Expected Continuation: Until 2025',
          'Similar to COL5A1, this gene plays a significant role in collagen synthesis and stability.',
        ],
      },
      {
        heading: 'COL3A1',
        lines: [
          'Score: 0.9',
          'Publications: 79',
          'Clinical Trials: None',
          'First Reported: 1986',
          'Expected Continuation: Until 2025',
          'COL3A1 is essential for the formation of type III collagen, which is vital for the structural integrity of various tissues.',
        ],
      },
      {
        heading: 'COL1A1',
        lines: [
          'Score: 0.9',
          'Publications: 35',
          'Clinical Trials: None',
          'First Reported: 1985',
          'Expected Continuation: Until 2025',
          'This gene encodes type I collagen, a major component of connective tissues, and is implicated in the pathology of Ehlers-Danlos syndrome.',
        ],
      },
    ],
    takeaway:
      "The genes associated with Ehlers-Danlos syndrome primarily encode collagen and related proteins, which are critical for maintaining the structural integrity of connective tissues. The high scores and substantial publication records underscore their significant roles in the disease's pathophysiology.",
  },
  parkinson: {
    question: 'Are there any known variants linked to parkinson disease?',
    title: 'Known Variants Linked to Parkinson Disease',
    context:
      'Context: There are 5,129 known variant-disease associations for Parkinson Disease (CUI: C0030567). Below are the first 5 associations with the highest scores and most publications.',
    sectionTitle: 'Key Variants',
    entries: [
      {
        heading: '🥇 Variant ID: rs34637584',
        lines: [
          'Gene Symbol: LRRK2',
          'Score: 1.0',
          'Publications: 400',
          'Year Range: 1993 - 2026',
          'Most Severe Consequence: missense_variant',
          'PolyPhen Score: 1.0',
          'SIFT Score: 0.0',
          'Top Chemicals in Evidence: Levodopa (CHEMBL1009, 2 publications), CA2 (CHEMBL2109588, 1 publication), 1-Methyl-4-phenylpyridinium (CHEMBL311617, 1 publication).',
        ],
      },
      {
        heading: '🥈 Variant ID: rs34778348',
        lines: [
          'Gene Symbol: LOC105369736, LRRK2',
          'Score: 0.9',
          'Publications: 84',
          'Year Range: 1993 - 2026',
          'Most Severe Consequence: missense_variant',
          'PolyPhen Score: 0.03',
          'SIFT Score: 0.03',
          'Top Chemicals in Evidence: Caffeine (CHEMBL113, 1 publication), Levodopa (CHEMBL1009, 1 publication), Idebenone (CHEMBL252556, 1 publication).',
        ],
      },
      {
        heading: '🥉 Variant ID: rs76763715',
        lines: [
          'Gene Symbol: GBA1',
          'Score: 0.9',
          'Publications: 41',
          'Year Range: 1993 - 2025',
          'Most Severe Consequence: missense_variant',
          'PolyPhen Score: 0.24',
          'SIFT Score: 0.02',
        ],
      },
      {
        heading: '4️⃣ Variant ID: rs356219',
        lines: [
          'Gene Symbol: LOC124900602',
          'Score: 0.9',
          'Publications: 28',
          'Year Range: 2008 - 2025',
          'Most Severe Consequence: intron_variant',
        ],
      },
      {
        heading: '5️⃣ Variant ID: rs2230288',
        lines: [
          'Gene Symbol: GBA1',
          'Score: 0.9',
          'Publications: 25',
          'Year Range: 2007 - 2026',
          'Most Severe Consequence: missense_variant',
          'PolyPhen Score: 0.02',
          'SIFT Score: 0.87',
        ],
      },
    ],
    takeaway:
      "These variants highlight significant genetic links to Parkinson Disease, with implications for understanding the disease's pathophysiology and potential therapeutic avenues.",
  },
  'type-2-diabetes': {
    question: 'Which chemicals are linked to type 2 diabetes?',
    title: 'Chemicals Linked to Type 2 Diabetes',
    context:
      'Context: There are 31 known chemicals associated with type 2 diabetes. Below are the first 10 chemicals with their relevant details.',
    sectionTitle: 'Key Chemicals and Their Associations',
    entries: [
      {
        heading: '💊 INSULIN GLARGINE',
        lines: [
          'Type: Protein',
          'Records: 243',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 2000',
        ],
      },
      {
        heading: '💊 INSULIN DETEMIR',
        lines: [
          'Type: Protein',
          'Records: 20',
          'Publications: 1',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 2004',
        ],
      },
      {
        heading: '💊 GLICLAZIDE',
        lines: [
          'Type: Small molecule',
          'Records: 20',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
        ],
      },
      {
        heading: '💊 METFORMIN',
        lines: [
          'Type: Small molecule',
          'Records: 144',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 1995',
        ],
      },
      {
        heading: '💊 METFORMIN HYDROCHLORIDE',
        lines: [
          'Type: Small molecule',
          'Records: 92',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 1995',
        ],
      },
      {
        heading: '💊 INSULIN ASPART',
        lines: [
          'Type: Protein',
          'Records: 85',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 2000',
        ],
      },
      {
        heading: '💊 DAPAGLIFLOZIN',
        lines: [
          'Type: Small molecule',
          'Records: 53',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 2012',
        ],
      },
      {
        heading: '💊 INSULIN LISPRO',
        lines: [
          'Type: Protein',
          'Records: 42',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 1996',
        ],
      },
      {
        heading: '💊 SEMAGLUTIDE',
        lines: [
          'Type: Protein',
          'Records: 27',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 2017',
        ],
      },
      {
        heading: '💊 EMPAGLIFLOZIN',
        lines: [
          'Type: Small molecule',
          'Records: 26',
          'Publications: 0',
          'Indication: Insulin-requiring type 2 diabetes mellitus',
          'First Approved: 2014',
        ],
      },
    ],
    takeaway:
      'The chemicals listed are primarily used in the management of insulin-requiring type 2 diabetes mellitus, with a mix of protein and small molecule types. While some have substantial records, many lack published studies supporting their indications.',
  },
};
